(() => {
  if (window.__CORGI_ANALYTICS_LOADED__) return;
  window.__CORGI_ANALYTICS_LOADED__ = true;

  const EVENT_PREFIX = 'corgi_';
  const LOCAL_CONSENT_KEY = 'corgi_cookie_consent';
  const LOCAL_CONSENT_COOKIE = 'corgi_cookie_consent';
  const CONSENT_KEYS = ['analytics', 'marketing', 'preferences'];
  const interactionMemory = new Set();

  function cleanValue(value) {
    if (value == null) return '';
    return String(value).replace(/\s+/g, ' ').trim().slice(0, 240);
  }

  function closest(element, selector) {
    return element && typeof element.closest === 'function' ? element.closest(selector) : null;
  }

  function getProductContext(element) {
    const root = closest(
      element,
      [
        '[data-product-id]',
        '[data-wishlist-product-gid]',
        '[data-corgi-product-handle]',
        '[data-corgi-wishlist-section]',
        '[data-corgi-sticky-add]',
        '[data-corgi-buy-button]',
        '[data-corgi-variant-selector]',
      ].join(',')
    );

    const productGid =
      cleanValue(element?.dataset?.wishlistProductGid) ||
      cleanValue(root?.dataset?.wishlistProductGid) ||
      cleanValue(element?.dataset?.productGid) ||
      cleanValue(root?.dataset?.productGid);
    const productId =
      cleanValue(element?.dataset?.productId) ||
      cleanValue(root?.dataset?.productId) ||
      productGid.replace(/^gid:\/\/shopify\/Product\//, '');
    const variantId =
      cleanValue(element?.dataset?.variantId) ||
      cleanValue(root?.dataset?.variantId) ||
      cleanValue(root?.dataset?.currentVariantId);
    const productHandle =
      cleanValue(element?.dataset?.productHandle) ||
      cleanValue(root?.dataset?.productHandle) ||
      cleanValue(document.body?.dataset?.productHandle);

    return {
      product_id: productId,
      product_gid: productGid,
      product_handle: productHandle,
      variant_id: variantId,
    };
  }

  function compactPayload(payload) {
    const result = {
      path: window.location.pathname,
      page_type: window.ShopifyAnalytics?.meta?.page?.pageType || window.Theme?.template?.name || '',
    };

    Object.entries(payload || {}).forEach(([key, value]) => {
      if (value == null || value === '') return;
      if (typeof value === 'boolean' || typeof value === 'number') {
        result[key] = value;
        return;
      }
      result[key] = cleanValue(value);
    });

    return result;
  }

  function normalizeConsentFlag(value) {
    if (value === true || value === 'true' || value === 'yes') return true;
    if (value === false || value === 'false' || value === 'no') return false;
    return null;
  }

  function normalizeStoredConsent(record) {
    if (!record || typeof record !== 'object') return null;

    const normalized = {};
    for (const key of CONSENT_KEYS) {
      const value = normalizeConsentFlag(record[key]);
      if (value == null) return null;
      normalized[key] = value;
    }

    return normalized;
  }

  function readCookieConsent() {
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(`${LOCAL_CONSENT_COOKIE}=`));

    if (!cookie) return null;

    try {
      return normalizeStoredConsent(JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('='))));
    } catch (_) {
      return null;
    }
  }

  function readLocalConsent() {
    const memoryConsent = normalizeStoredConsent(window.CorgiCookieConsentFallback);
    if (memoryConsent) return memoryConsent;

    try {
      const stored = window.localStorage?.getItem(LOCAL_CONSENT_KEY);
      if (stored) {
        const normalized = normalizeStoredConsent(JSON.parse(stored));
        if (normalized) return normalized;
      }
    } catch (_) {}

    return readCookieConsent();
  }

  function getShopifyAnalyticsConsent(privacyApi) {
    if (!privacyApi || typeof privacyApi.currentVisitorConsent !== 'function') return '';

    const consent = privacyApi.currentVisitorConsent() || {};
    if (consent.analytics === 'no') return 'denied';
    if (consent.analytics === 'yes') return 'allowed';
    return '';
  }

  function canPublishToShopifyAnalytics() {
    const privacyApi = window.Shopify?.customerPrivacy;
    const visibleCookieBanner = document.querySelector('[data-corgi-cookie-consent]:not([hidden])');
    const shopifyAnalyticsConsent = getShopifyAnalyticsConsent(privacyApi);
    const localConsent = readLocalConsent();

    if (shopifyAnalyticsConsent === 'denied') return false;

    if (shopifyAnalyticsConsent === 'allowed') {
      if (typeof privacyApi.analyticsProcessingAllowed === 'function' && !privacyApi.analyticsProcessingAllowed()) return false;
      if (typeof privacyApi.userCanBeTracked === 'function' && !privacyApi.userCanBeTracked()) return false;
      return true;
    }

    if (localConsent) return Boolean(localConsent.analytics);

    if (!privacyApi) return !visibleCookieBanner;

    if (typeof privacyApi.analyticsProcessingAllowed === 'function' && !privacyApi.analyticsProcessingAllowed()) return false;
    if (typeof privacyApi.userCanBeTracked === 'function' && !privacyApi.userCanBeTracked()) return false;
    return true;
  }

  function publish(name, payload = {}) {
    const eventName = String(name || '').startsWith(EVENT_PREFIX) ? String(name) : `${EVENT_PREFIX}${name}`;
    const eventPayload = compactPayload(payload);

    document.dispatchEvent(
      new CustomEvent('corgi:analytics', {
        bubbles: true,
        detail: { name: eventName, payload: eventPayload },
      })
    );

    if (
      canPublishToShopifyAnalytics() &&
      window.Shopify?.analytics &&
      typeof window.Shopify.analytics.publish === 'function'
    ) {
      window.Shopify.analytics.publish(eventName, eventPayload);
      return true;
    }

    return false;
  }

  function trackOnce(key, name, payload) {
    if (!key || interactionMemory.has(key)) return;
    interactionMemory.add(key);
    publish(name, payload);
  }

  function trackWishlistClick(button) {
    const isSaved = button.getAttribute('aria-pressed') === 'true' || button.classList.contains('is-wishlisted');
    publish(isSaved ? 'wishlist_remove_intent' : 'wishlist_add_intent', {
      ...getProductContext(button),
      source: cleanValue(button.dataset.wishlistSource || button.dataset.source || 'wishlist_button'),
    });
  }

  function trackQuickAddClick(button) {
    publish('quick_add_click', {
      ...getProductContext(button),
      variant_id: cleanValue(button.dataset.variantId) || getProductContext(button).variant_id,
      source: cleanValue(button.dataset.corgiQuickSource || button.dataset.source || 'quick_add'),
    });
  }

  function trackStockAlertIntent(trigger) {
    const input = trigger.matches?.('input') ? trigger : trigger.closest?.('label')?.querySelector?.('input');
    if (input instanceof HTMLInputElement && input.dataset.optionAvailable !== 'false') return;

    publish('stock_alert_open_intent', {
      ...getProductContext(trigger),
      option: cleanValue(input?.value || trigger.textContent),
      source: 'variant_selector',
    });
  }

  function trackCalculatorInteraction(target) {
    const root = closest(target, '.corgi-pagecalc, [data-corgi-pagecalc], [data-corgicalc], [data-corgi-calc]');
    if (!root) return;

    const mode =
      cleanValue(root.dataset.mode) ||
      cleanValue(root.dataset.corgiPagecalc) ||
      cleanValue(root.querySelector?.('[data-mode]')?.dataset?.mode) ||
      'calculator';
    const sectionId = cleanValue(root.dataset.sectionId || root.id || root.dataset.corgiPagecalc || '');
    trackOnce(`calc:${sectionId}:${mode}`, 'calculator_interaction', {
      mode,
      section_id: sectionId,
      source: cleanValue(target.name || target.id || target.dataset.freeModeBtn || target.dataset.panel || 'calculator'),
    });
  }

  function trackCompareInteraction(target) {
    const root = closest(target, '[id^="CorgiCompare"], [id^="CorgiComparador"], [data-corgi-comparador], [data-corgi-compare]');
    if (!root) return;
    const sectionId = cleanValue(root.id || root.dataset.sectionId || '');
    trackOnce(`compare:${sectionId}`, 'compare_interaction', {
      section_id: sectionId,
      source: cleanValue(target.dataset.cc2QiTrigger ? 'que_incluye_preview' : target.name || target.id || 'compare'),
    });
  }

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const wishlistButton = closest(target, '[data-corgi-wishlist-button]');
      if (wishlistButton) trackWishlistClick(wishlistButton);

      const quickAddButton = closest(target, '[data-corgi-card-quick-add], [data-corgi-mobile-quick-add-option], [data-corgi-wishlist-add-cart]');
      if (quickAddButton) trackQuickAddClick(quickAddButton);

      const mobileQuickTrigger = closest(target, '[data-corgi-mobile-quick-trigger]');
      if (mobileQuickTrigger) {
        publish('quick_add_open', {
          ...getProductContext(mobileQuickTrigger),
          source: 'mobile_quick_add',
        });
      }

      const stockAlertTrigger = closest(target, '[data-corgi-stock-alert-trigger]');
      if (stockAlertTrigger) trackStockAlertIntent(stockAlertTrigger);

      const shareButton = closest(target, '[data-corgi-product-share]');
      if (shareButton) {
        publish('product_share_click', {
          ...getProductContext(shareButton),
          source: 'product_actions',
        });
      }

      const previewTrigger = closest(
        target,
        '[data-corgi-incluye-preview], [data-compare-preview], [data-cc2-qi-trigger], [data-corgi-refacciones-preview]'
      );
      if (previewTrigger) {
        publish('preview_open_intent', {
          ...getProductContext(previewTrigger),
          source: previewTrigger.hasAttribute('data-corgi-incluye-preview')
            ? 'que_incluye'
            : previewTrigger.hasAttribute('data-corgi-refacciones-preview')
              ? 'refacciones'
              : 'comparador',
        });
      }

      const details = closest(target, 'details[data-corgi-accordion-row]');
      if (details && !details.open) {
        publish('accordion_open', {
          row: cleanValue(details.dataset.corgiAccordionRow),
          source: 'corgi_accordion',
        });
      }

      trackCalculatorInteraction(target);
      trackCompareInteraction(target);
    },
    true
  );

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    trackCalculatorInteraction(target);
    trackCompareInteraction(target);
  });

  document.addEventListener('cart:update', (event) => {
    const detail = event.detail || {};
    const source = cleanValue(detail?.data?.source || detail?.source || '');
    if (!source || !source.includes('corgi')) return;

    publish('cart_add_success', {
      source,
      variant_id: cleanValue(detail?.data?.variantId || detail?.sourceId || ''),
      item_count: detail?.data?.itemCount || 1,
    });
  });

  window.CorgiAnalytics = {
    track: publish,
    trackOnce,
  };
})();
